import { useState } from 'react'
import { CalendarClock, CheckCircle2, Clock, History, Repeat, Users, Wallet, X } from 'lucide-react'
import { cn } from '@/utils/cn'
import { formatCurrency } from '@/utils/format-currency'
import { PARTNER_COMMISSION_MODE_LABEL } from '@/components/Partners/Modals/partnerDetailModalConstants'
import type { PartnerCommissionStatus, PartnerPaymentHistoryRow, PartnerPaymentsSummaryRow } from '@/types'
import { PartnerCommissionsTable } from './PartnerCommissionsTable'
import { usePartnerPayments } from './usePartnerPayments'

type PaymentsTab = 'partners' | 'commissions' | 'history'

interface PartnersPaymentsViewProps {
  version: number
  search: string
  canApprove: boolean
  canPay: boolean
  onChanged: () => Promise<void>
  onOpenPartner: (partnerId: string) => void
}

const SELECT_CLASS =
  'rounded-md border border-[var(--border)] bg-[var(--bg-main)] px-3 py-2 text-[13px] text-[var(--text-primary)] outline-none focus:border-[var(--brand-primary)]'

const formatDate = (value: string | null) => (value ? new Date(value).toLocaleDateString('pt-BR') : '—')

function SummaryCard({ label, value, hint, icon: Icon, tone }: {
  label: string
  value: number
  hint?: string
  icon: typeof Wallet
  tone: string
}) {
  return (
    <div className='glass-card p-4'>
      <div className='mb-2 flex items-center gap-2'>
        <div className={cn('flex h-8 w-8 items-center justify-center rounded-lg', tone)}>
          <Icon size={16} />
        </div>
        <p className='font-medium uppercase tracking-wider text-[var(--font-color-label)]' style={{ fontSize: 'var(--font-size-label)' }}>
          {label}
        </p>
      </div>
      <p className='text-xl font-black text-[var(--font-color-body)]'>{formatCurrency(value)}</p>
      {hint && <p className='mt-0.5 text-[11px] text-[var(--font-color-muted)]'>{hint}</p>}
    </div>
  )
}

function TableHead({ columns }: { columns: string[] }) {
  return (
    <thead>
      <tr className='border-b border-[var(--glass-border)]'>
        {columns.map((h) => (
          <th key={h} className='px-4 py-3 text-left font-medium uppercase tracking-wider text-[var(--font-color-label)]' style={{ fontSize: 'var(--font-size-label)' }}>
            {h}
          </th>
        ))}
      </tr>
    </thead>
  )
}

function PartnersSummaryTable({ rows, onFilter, onOpenPartner }: {
  rows: PartnerPaymentsSummaryRow[]
  onFilter: (partnerId: string, status: '' | PartnerCommissionStatus) => void
  onOpenPartner: (partnerId: string) => void
}) {
  return (
    <div className='glass-card overflow-hidden p-0'>
      <div className='overflow-x-auto'>
        <table className='w-full'>
          <TableHead columns={['Parceiro', 'Pendente', 'A pagar', 'Pago (total)', 'Próx. mês (recorrente)', 'Último pagamento', 'Pix']} />
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className='border-b border-[var(--glass-border)] last:border-0 hover:bg-[var(--glass-bg)] transition-colors'>
                <td className='px-4 py-3'>
                  <button type='button' className='text-left' onClick={() => onOpenPartner(row.id)}>
                    <p className='text-sm font-medium text-[var(--font-color-body)] hover:underline'>{row.nome}</p>
                    <p className='text-[11px] text-[var(--font-color-muted)]'>
                      {PARTNER_COMMISSION_MODE_LABEL[row.modoComissao] ?? row.modoComissao} · {row.comissao}%
                      {!row.indicacaoAtiva && ' · inativo'}
                    </p>
                  </button>
                </td>
                <td className='px-4 py-3'>
                  <button type='button' disabled={row.pendentesCount === 0} onClick={() => onFilter(row.id, 'pendente')} className='text-left disabled:cursor-default'>
                    <span className='text-sm text-[var(--font-color-body)]'>{formatCurrency(row.pendente)}</span>
                    {row.pendentesCount > 0 && <span className='block text-[10px] text-[var(--font-color-muted)]'>{row.pendentesCount} comissão(ões)</span>}
                  </button>
                </td>
                <td className='px-4 py-3'>
                  <button type='button' disabled={row.aprovadasCount === 0} onClick={() => onFilter(row.id, 'aprovada')} className='text-left disabled:cursor-default'>
                    <span className={cn('text-sm font-bold', row.aprovada > 0 ? 'text-amber-500' : 'text-[var(--font-color-body)]')}>
                      {formatCurrency(row.aprovada)}
                    </span>
                    {row.aprovadasCount > 0 && <span className='block text-[10px] text-[var(--font-color-muted)]'>{row.aprovadasCount} comissão(ões)</span>}
                  </button>
                </td>
                <td className='px-4 py-3'>
                  <button type='button' onClick={() => onFilter(row.id, 'paga')} className='text-sm text-emerald-500'>
                    {formatCurrency(row.paga)}
                  </button>
                </td>
                <td className='px-4 py-3 text-sm text-[var(--font-color-body)]'>
                  {row.recorrenciaMensal > 0 ? formatCurrency(row.recorrenciaMensal) : '—'}
                </td>
                <td className='px-4 py-3 text-sm text-[var(--font-color-body)]'>{formatDate(row.ultimoPagamentoEm)}</td>
                <td className='px-4 py-3 text-xs text-[var(--font-color-muted)]'>{row.chavePix || '—'}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className='px-4 py-10 text-center text-sm text-[var(--font-color-muted)]'>Nenhum parceiro encontrado</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function PaymentHistoryTable({ rows, loading }: { rows: PartnerPaymentHistoryRow[]; loading: boolean }) {
  const total = rows.reduce((sum, r) => sum + r.valor, 0)
  return (
    <div className='glass-card overflow-hidden p-0'>
      <div className='border-b border-[var(--glass-border)] px-4 py-3 text-xs text-[var(--font-color-muted)]'>
        {rows.length} pagamento(s) · total <strong className='text-[var(--font-color-body)]'>{formatCurrency(total)}</strong>
      </div>
      <div className='overflow-x-auto'>
        <table className='w-full'>
          <TableHead columns={['Data', 'Parceiro', 'Valor', 'Método', 'Notas']} />
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className='border-b border-[var(--glass-border)] last:border-0'>
                <td className='px-4 py-3 text-sm text-[var(--font-color-body)]'>{formatDate(row.dataPagamento)}</td>
                <td className='px-4 py-3 text-sm font-medium text-[var(--font-color-body)]'>{row.parceiro.nome}</td>
                <td className='px-4 py-3 text-sm font-bold text-emerald-500'>{formatCurrency(row.valor)}</td>
                <td className='px-4 py-3 text-sm text-[var(--font-color-body)]'>{row.metodo || '—'}</td>
                <td className='px-4 py-3 text-xs text-[var(--font-color-muted)]'>
                  {row.notas || '—'}
                  {row.comprovanteUrl && (
                    <a href={row.comprovanteUrl} target='_blank' rel='noreferrer' className='ml-2 text-[var(--brand-primary)] hover:underline'>
                      comprovante
                    </a>
                  )}
                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={5} className='px-4 py-10 text-center text-sm text-[var(--font-color-muted)]'>Nenhum pagamento registrado</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export function PartnersPaymentsView({ version, search, canApprove, canPay, onChanged, onOpenPartner }: PartnersPaymentsViewProps) {
  const [tab, setTab] = useState<PaymentsTab>('partners')
  const payments = usePartnerPayments(version, onChanged)
  const { filters, setFilters, summary } = payments

  const partnerRows = (summary?.partners ?? []).filter((p) => p.nome.toLowerCase().includes(search.toLowerCase()))
  const hasFilters = Boolean(filters.parceiroId || filters.status || filters.fromMonth || filters.toMonth)

  const filterBy = (parceiroId: string, status: '' | PartnerCommissionStatus) => {
    setFilters((prev) => ({ ...prev, parceiroId, status }))
    setTab('commissions')
  }

  const tabs = [
    { id: 'partners', label: 'Por parceiro', icon: Users },
    { id: 'commissions', label: 'Comissões', icon: Wallet },
    { id: 'history', label: 'Histórico de pagamentos', icon: History },
  ] as const

  return (
    <div className='space-y-4'>
      <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4'>
        <SummaryCard label='Pendente' value={summary?.totals.pendente ?? 0} hint='Aguardando aprovação' icon={Clock} tone='bg-[var(--glass-bg)] text-[var(--text-muted)]' />
        <SummaryCard label='A pagar' value={summary?.totals.aprovada ?? 0} hint='Aprovadas, prontas para repasse' icon={CalendarClock} tone='bg-amber-500/10 text-amber-500' />
        <SummaryCard label='Pago' value={summary?.totals.paga ?? 0} hint='Total já repassado' icon={CheckCircle2} tone='bg-emerald-500/10 text-emerald-500' />
        <SummaryCard label='Recorrência mensal' value={summary?.totals.recorrenciaMensal ?? 0} hint='Previsão de comissões futuras por mês' icon={Repeat} tone='bg-indigo-500/10 text-indigo-400' />
      </div>

      <div className='glass-card flex flex-wrap items-end gap-3'>
        <div className='flex rounded-lg border border-[var(--border)] p-0.5' role='tablist' aria-label='Pagamentos'>
          {tabs.map((option) => (
            <button
              key={option.id}
              type='button'
              role='tab'
              aria-selected={tab === option.id}
              onClick={() => setTab(option.id)}
              className={cn(
                'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                tab === option.id ? 'bg-[var(--brand-primary)] text-white' : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]',
              )}
            >
              <option.icon size={13} />
              {option.label}
            </button>
          ))}
        </div>

        {tab !== 'partners' && (
          <div className='flex flex-1 flex-wrap items-end justify-end gap-2'>
            <select
              aria-label='Filtrar por parceiro'
              value={filters.parceiroId}
              onChange={(e) => setFilters((prev) => ({ ...prev, parceiroId: e.target.value }))}
              className={SELECT_CLASS}
            >
              <option value=''>Todos os parceiros</option>
              {(summary?.partners ?? []).map((p) => (
                <option key={p.id} value={p.id}>{p.nome}</option>
              ))}
            </select>
            {tab === 'commissions' && (
              <select
                aria-label='Filtrar por status'
                value={filters.status}
                onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value as '' | PartnerCommissionStatus }))}
                className={SELECT_CLASS}
              >
                <option value=''>Todos os status</option>
                <option value='pendente'>Pendente</option>
                <option value='aprovada'>A pagar</option>
                <option value='paga'>Paga</option>
              </select>
            )}
            <label className='flex flex-col gap-1 text-[10px] uppercase tracking-wider text-[var(--font-color-label)]'>
              De
              <input type='month' value={filters.fromMonth} onChange={(e) => setFilters((prev) => ({ ...prev, fromMonth: e.target.value }))} className={SELECT_CLASS} />
            </label>
            <label className='flex flex-col gap-1 text-[10px] uppercase tracking-wider text-[var(--font-color-label)]'>
              Até
              <input type='month' value={filters.toMonth} onChange={(e) => setFilters((prev) => ({ ...prev, toMonth: e.target.value }))} className={SELECT_CLASS} />
            </label>
            {hasFilters && (
              <button
                type='button'
                onClick={() => setFilters({ parceiroId: '', status: '', fromMonth: '', toMonth: '' })}
                className='flex items-center gap-1 rounded-md px-2 py-2 text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)]'
              >
                <X size={12} /> Limpar
              </button>
            )}
          </div>
        )}
      </div>

      {tab === 'partners' && <PartnersSummaryTable rows={partnerRows} onFilter={filterBy} onOpenPartner={onOpenPartner} />}
      {tab === 'commissions' && (
        <PartnerCommissionsTable
          rows={payments.commissions}
          loading={payments.loading}
          busy={payments.busy}
          canApprove={canApprove}
          canPay={canPay}
          meta={payments.commissionsMeta}
          page={payments.page}
          onPageChange={payments.setPage}
          onUpdateStatus={payments.updateStatus}
          onRegisterPayment={payments.registerPayment}
        />
      )}
      {tab === 'history' && <PaymentHistoryTable rows={payments.history} loading={payments.loading} />}
    </div>
  )
}
